<template>
  <div class="quick-add">
    <button
      v-if="product"
      class="quick-add__add-to-cart"
      :class="{
        [$class('success')]: state === 'added',
        [$class('error')]: state === 'error',
        'quick-add__add-to-cart--label': Boolean(quickAddLabel),
      }"
      :disabled="!isCardAvailable || (disableCta && state !== 'adding')"
      type="button"
      @click.prevent.stop="checkAddToCart"
    >
      <span
        v-if="!quickAddLabel"
        class="visually-hidden"
        v-text="$string('product.cta.add_to_cart')"
      />
      <span
        v-else
        class="quick-add__label text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
        v-text="quickAddLabel"
      />
      <component :is="addToCartIcon" />
    </button>

    <teleport to="body">
      <overlay
        v-if="
          (useProductState ||
            variantsShowQatcOverlay ||
            template === 'card-compact') &&
          product &&
          selectedVariant
        "
        :ref="`quickAddModal-${product.handle}`"
        block-class="quick-add-select-options"
        direction="up"
        :namespace="modalNamespace"
        type="shelf-modal"
        shelf-close
        :ignore-close="dropdownIsOpen"
      >
        <template #title>
          <span
            class="quick-add__overlay-title"
            v-text="$string('product.quick_add.modal_title')"
          />
        </template>
        <template #body="overlay">
          <a class="quick-add-select-options__content" :href="product.url">
            <div class="quick-add-select-options__image-container">
              <responsive-image
                class="quick-add-select-options__image"
                :image="featuredImage"
                ratio="1:1"
                sizes="(min-width: 1024px) 178px, 120px"
                widths="178, 356, 534"
              />
            </div>

            <div class="quick-add-select-options__title">
              <p
                class="product-card-select__title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
                v-text="product.title"
              />

              <product-prices
                class="product-card-select__prices"
                :compare-at-price="selectedVariant?.compareAtPrice?.amount"
                :discount-logic="getDiscountLogic(selectedVariant)"
                :price="selectedVariant?.price?.amount"
                template="card"
                :unit-price="selectedVariant?.unitPrice?.amount"
                :unit-price-measurement="selectedVariant?.unitPriceMeasurement"
                :is-gift-card="product.giftCard"
              />
            </div>
          </a>

          <form
            class="quick-add-select-options__form"
            @submit.prevent="handleUpdateSelectedVariant"
          >
            <product-options
              v-if="overlay.isActive || isVariantSelectorActive"
              :key="`quickAddModal-${product.handle}`"
              class="product-form__options"
              :loading="loading"
              :product="effectiveProduct"
              :section="{
                id: product.id,
                optionStylingTile: $variable(
                  'product.overlayProductOptionsTile',
                ),
                optionStylingImageTile: [],
              }"
              :variant="selectedVariant"
              :variant-from-url="false"
              hide-count-label
              hide-portions-label
              hide-sold-out-label
              hide-flavor-image-on-button
              hide-flavor-badge-on-button
              style-full-size
              :hide-labels="false"
              @update-variant="handleUpdateOption"
              @dropdown-toggled="
                (isOpen) => {
                  dropdownIsOpen = isOpen;
                }
              "
              @variant-selector-namespace="
                (namespace) => {
                  variantSelectorNamespace = namespace;
                }
              "
            />

            <notify-me
              v-if="showNotify"
              ref="notifyMe"
              class="quick-add-select-options__notify-me"
              :variant-id="selectedVariant.id"
              :product-id="product.id"
              @update-success="handleNotifyMeSuccess"
            />

            <p
              v-if="selectedVariantLimitHint"
              class="quick-add-select-options__limit-hint text-mobile-paragraph-s-regular"
              role="status"
              v-text="selectedVariantLimitHint"
            />

            <div
              v-if="showCta"
              class="quick-add-select-options__button-wrapper"
            >
              <btn
                class="quick-add-select-options__save"
                :class="{
                  [`quick-add-${template}`]: template,
                  'button--notify-me': variant && !selectedVariant.available,
                }"
                :disabled="Boolean(selectedVariantLimitHint)"
                :label="addToCartText"
                modifiers="secondary cta center no-hover"
                :show-state-icon="state === 'add_notify'"
                :show-icon="layerAddToCartIcon !== ''"
                type="submit"
                @click="handleAddToCart"
              >
                <template #icon>
                  <component :is="layerAddToCartIcon" />
                </template>

                <template #stateIcon>
                  <icon-loading />
                </template>
              </btn>
            </div>
          </form>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: quick-add (quick-add)
 * -----------------------------------------------------------------------------
 * Quick add to card button for product cards.
 * - Component settings: snippet / dynamic / async / scroll
 *
 */
import { mapActions, mapState } from 'pinia';
import useToastNotificationsStore from '~stores/toast-notifications';
import useOverlaysStore from '~stores/overlays';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

import Btn from '~global/btn/btn';
import IconAdd from '~icons/general/add.svg';
import IconCheck from '~icons/general/check.svg';
import IconCross from '~icons/general/cross.svg';
import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';
import IconEmail from '~icons/general/email.svg';
import IconLoading from '~icons/general/loading.svg';
import NotifyMe from '~async/product-form/notify-me';
import Overlay from '~global/overlay/overlay';
import ProductOptions from '~async/product-form/product-options';
import ProductBadges from '~global/product-badges/product-badges';
import ProductPrices from '~global/product-prices/product-prices';
import ResponsiveImage from '~global/images/responsive-image';

import { formatTrackingItem } from '~/format/format-tracking-item';
import { computeDiscountLogic, emitUpdateVariant } from '~/helpers/vue';
import { getMaxQuantityErrorMessage } from '~/helpers/general';
import { loadRemainingFormattedVariants } from '~/helpers/product-variants';
import { triggerKameleoonEvent } from '~/helpers/kameleoon';

export default {
  name: 'QuickAdd',

  components: {
    Btn,
    IconAdd,
    IconCheck,
    IconChevronDown,
    IconCross,
    IconEmail,
    IconLoading,
    NotifyMe,
    Overlay,
    ProductOptions,
    ProductBadges,
    ProductPrices,
    ResponsiveImage,
  },

  inject: {
    variantsShowQatcOverlay: {
      from: 'variantsShowQatcOverlay',
      default: true,
    },
    sectionId: {
      from: 'sectionId',
      default: false,
    },
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    location: {
      type: String,
      default: 'Quick Add To Cart',
    },
    useProductState: {
      type: Boolean,
      default: false,
    },
    campaignCard: {
      type: Boolean,
      default: false,
    },
    freebieHandle: {
      type: [Boolean, String],
      default: false,
    },
    freebieGroupUid: {
      type: String,
      default: null,
    },
    showPageToast: {
      type: Boolean,
      default: true,
    },
    showCartToast: {
      type: Boolean,
      default: false,
    },
    template: {
      type: String,
      default: '',
    },
    quickAddLabel: {
      type: String,
      default: '',
    },
    slideIndex: {
      type: Number,
      default: 0,
    },
    disableCartAdd: {
      type: Boolean,
      default: false,
    },
    // Only with `disableCartAdd`: hint text keyed by variant id for variants
    // the caller can't take another unit of (e.g. mix & match stock limit).
    // The selected variant's CTA is disabled and shows its hint instead.
    limitHints: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: {
    'track-click': () => true,
    'update-variant': emitUpdateVariant,
    'added-to-cart': (payload) => typeof payload === 'number',
    'confirm-local-add': (variant) =>
      typeof variant === 'object' && variant !== null,
  },

  data() {
    return {
      // only 9 characters to keep it short, since it's used in the DOM
      uid: Math.random().toString(36).slice(2, 11),
      state: 'ready',
      selected: true,
      selectedVariant: false,
      showCta: true,
      // Keyed by product handle (not a single shared slot) so a card whose
      // `product` prop is reused/reassigned across re-renders (e.g. an
      // index-keyed carousel reordering) can't have another handle's watcher
      // clear out - or another handle's fetch race with - this one's state.
      // See `effectiveProduct`, `loadRemainingVariantsIfNeeded()` and
      // `~/helpers/product-variants.js`.
      enrichedProductsByHandle: {},
      loadingVariantHandles: {},
      dropdownIsOpen: false,
      variantSelectorNamespace: '',
    };
  },

  computed: {
    /**
     * Hint for the selected variant when the caller can't take another unit
     * of it (see `limitHints`) — empty otherwise. Only applies to local adds.
     * @returns {String}
     */
    selectedVariantLimitHint() {
      if (!this.disableCartAdd || !this.selectedVariant) {
        return '';
      }

      return this.limitHints[String(this.selectedVariant.id)] || '';
    },

    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),
    ...mapState(useCartStore, {
      cartStatus: 'status',
      depositItemUpdate: 'depositItemUpdate',
    }),
    ...mapState(useOverlaysStore, ['getActiveOverlay']),

    isCardAvailable() {
      // If the product is a campaign card or uses product state, we check the variants for availability.
      if (this.useProductState || this.campaignCard) {
        return this.product?.variants?.some(
          (variant) =>
            variant.available && variant.availableState === 'available',
        );
      }

      const state = this.initialSelectedVariant;

      return !(
        state?.availableState === 'back-soon' ||
        state?.availableState === 'sold-out' ||
        state?.availableState === 'hidden'
      );
    },

    /**
     * Compute add to cart icon.
     * @returns {String}
     */
    addToCartIcon() {
      switch (this.state) {
        case 'adding':
          return 'icon-loading';
        case 'added':
          return 'icon-check';
        case 'error':
          return 'icon-cross';
        default:
          return this.quickAddLabel ? null : 'icon-add';
      }
    },

    /**
     * Compute cta button icon.
     * @returns {String}
     */
    layerAddToCartIcon() {
      if (this.showNotify) {
        return 'icon-email';
      }
      return '';
    },

    disableCta() {
      return Boolean(
        this.selectedVariant?.id &&
        this.cartStatus.items.includes(this.selectedVariant.id),
      );
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.selectedVariant) {
        return this.$string('product.cta.select_variant');
      }

      if (this.variant && !this.selectedVariant?.available) {
        return this.$string('product.cta.notify_me');
      }

      return this.$string('product.cta.to_cart');
    },

    /**
     * Checks whether to use variant first image.
     * - Use variant featured image (grey background) in product grid.
     * @returns {Object}
     */
    featuredImage() {
      if (this.selectedVariant) {
        if (
          this.selectedVariant.media &&
          this.selectedVariant.media.length > 0
        ) {
          return this.selectedVariant.media[0].image;
        }

        if (this.selectedVariant.localizedImage) {
          return this.selectedVariant.localizedImage;
        }

        if (
          this.selectedVariant.featuredImage &&
          Object.keys(this.selectedVariant.featuredImage).length > 0
        ) {
          return this.selectedVariant.featuredImage;
        }
      }

      if (this.product.media && this.product.media.length > 0) {
        return this.product.media[0].image;
      }

      return this.product.featuredImage;
    },

    /**
     * Checks if the current variant is available.
     * @returns {boolean} True if the variant exists and is available.
     */
    isSelectedVariantAvailable() {
      return this.selectedVariant?.available;
    },

    /**
     * Checks if the current variant's state is either 'back-soon'
     * or 'sold-out'.
     * @returns {boolean} True if the variant's availableState is 'back-soon'
     * or 'sold-out'.
     */
    isSelectedVariantBackSoonOrSoldOut() {
      return ['back-soon', 'sold-out'].includes(
        this.selectedVariant?.availableState,
      );
    },

    /**
     * Checks if only one of the product's variants is available.
     * @returns {Boolean}
     */
    hasOnlyOneAvailableVariant() {
      return (
        this.product?.variants?.filter(
          (variant) =>
            variant.available && variant.availableState === 'available',
        ).length === 1
      );
    },

    /**
     * Checks if the product genuinely only has one variant to choose from,
     * e.g. a single size with no flavor options
     * - Filters hidden variants because they are always excluded
     * @returns {Boolean}
     */
    hasOnlyOneVariant() {
      return (
        this.product?.variants?.filter(
          (variant) => variant.availableState !== 'hidden',
        ).length === 1
      );
    },

    /**
     * Checks if notify me form should be shown instead of add to cart.
     * @returns {boolean} True if the variant exists and is available.
     */
    showNotify() {
      return (
        !this.isSelectedVariantAvailable ||
        this.isSelectedVariantBackSoonOrSoldOut
      );
    },

    modalNamespace() {
      const sectionIdSubstr = this.sectionId ? `${this.sectionId}-` : '';
      return `quickAddModal-${sectionIdSubstr}${this.product.handle}-${this.variant.id}-${this.uid}`;
    },

    isVariantSelectorActive() {
      return this.getActiveOverlay?.namespace === this.variantSelectorNamespace;
    },

    /**
     * Use the product's default selectable variant for product-based cards.
     * Keep the passed variant for variant-specific cards.
     * @returns {Boolean|Object}
     */
    initialSelectedVariant() {
      if (this.useProductState || !this.variant) {
        return this.product?.selectedOrFirstAvailableVariant;
      }

      return this.variant;
    },

    /**
     * Product passed to `product-options` - the plain `product` prop until
     * `loadRemainingVariantsIfNeeded()` has finished paginating (products
     * with 250+ variants, e.g. Isoclear's Flavor x Sweetness x Size combos -
     * see wiki/domain/pdp-sweetness-selector.md).
     * @returns {Object}
     */
    effectiveProduct() {
      const handle = this.product?.handle;
      return (handle && this.enrichedProductsByHandle[handle]) || this.product;
    },
  },

  watch: {
    /**
     * Watch current variant to update form's selected variant.
     */
    selectedVariant() {
      this.soldOut = Boolean(!this.selectedVariant?.available);

      this.showCta =
        !this.$refs.notifyMe ||
        (this.$refs.notifyMe &&
          !this.$refs.notifyMe.submittedVariantIds.includes(
            this.selectedVariant?.id,
          ));
    },

    /**
     * Watch product to update current variant.
     */
    product() {
      this.selectedVariant = this.initialSelectedVariant;

      // No enrichment/in-flight state to reset here - both are keyed by
      // handle (see `enrichedProductsByHandle`/`loadingVariantHandles`), so
      // swapping to a different product (e.g. an index-keyed carousel
      // reordering) just makes `effectiveProduct` look up a different key
      // instead of colliding with whatever this component instance was
      // already doing for the previous handle.
    },
    /**
     * Watch variant to update current variant.
     */
    variant() {
      this.selectedVariant = this.initialSelectedVariant;
    },
  },

  mounted() {
    // Set initial variant on mount.
    this.selectedVariant = this.initialSelectedVariant;

    this.soldOut = Boolean(!this.selectedVariant?.available);
  },

  methods: {
    ...mapActions(useCartStore, {
      addItem: 'addItem',
      setDepositItemUpdate: 'setDepositItemUpdate',
      getCartAttributeCode: 'getCartAttributeCode',
    }),
    ...mapActions(useToastNotificationsStore, ['addToast']),
    ...mapActions(useOverlaysStore, {
      openOverlay: 'open',
      closeOverlay: 'close',
    }),

    /**
     * Handle update variant event.
     * @param {Object} variant - Emitted variant object.
     */
    handleUpdateOption(variant) {
      this.selectedVariant = variant;
      this.$emit('update-variant', variant);
    },

    /**
     * Reset the overlay variant back to the initial card variant.
     */
    resetSelectedVariant() {
      this.selectedVariant = this.initialSelectedVariant;
    },

    /**
     * Fetch and merge in any variant pages beyond the product's initial
     * `variants(first: 250)` fetch, so option-picking (e.g. the sweetness
     * selector's sold-out checks) sees every variant instead of only the
     * first page - see `~/helpers/product-variants.js`.
     * - No-op when the product's initial fetch already had every variant,
     *   when this handle is already fully enriched (e.g. the overlay was
     *   closed and reopened), or while a fetch for this exact handle is
     *   already in flight. Deliberately keyed by `handle`, not "the current
     *   product" - see `enrichedProductsByHandle`/`loadingVariantHandles`.
     * - Not aborted if `this.product` changes while this is in flight (e.g.
     *   an index-keyed carousel reordering): letting it run to completion
     *   and cache its result under its own handle is simpler and no less
     *   correct than trying to cancel it, and means the result is still
     *   there if this handle's card reappears later.
     */
    async loadRemainingVariantsIfNeeded() {
      const { handle, variantsIncomplete } = this.product || {};
      const cached = handle && this.enrichedProductsByHandle[handle];

      if (
        !variantsIncomplete ||
        this.loadingVariantHandles[handle] ||
        (cached && !cached.variantsIncomplete)
      ) {
        return;
      }

      this.loadingVariantHandles[handle] = true;

      try {
        this.enrichedProductsByHandle[handle] =
          await loadRemainingFormattedVariants(this.product);
      } catch {
        // `loadRemainingFormattedVariants()` already reports load failures
        // itself before its promise rejects - not re-reporting here avoids
        // double-reporting the same failure and, since `cnvs.ReportError`
        // throws, an unhandled rejection on this fire-and-forget call. The
        // next `loadRemainingVariantsIfNeeded()` call for this handle (e.g.
        // reopening the overlay) will simply retry, since nothing gets
        // cached on failure.
      } finally {
        delete this.loadingVariantHandles[handle];
      }
    },

    /**
     * Get discount logic.
     * @param {Boolean|Object} variant - Variant object to use.
     * @returns {Object}
     */
    getDiscountLogic(variant) {
      return computeDiscountLogic({
        discountPreview: false,
        onSale: variant?.compareAtPriceDiscount,
        product: this.product,
        variant,
      });
    },

    /**
     * Checks on first button click if product should go directly to cart.
     * Or if we have to show variant selection layer.
     *
     * @param {Object} event - Event data object.
     * @param {Boolean} forceOverlay - Flag to force show overlay.
     */
    async checkAddToCart(event, forceOverlay = false) {
      if (this.state === 'adding') {
        return;
      }
      this.$emit('track-click');

      if (
        !forceOverlay &&
        (this.product.hasOnlyDefaultVariant ||
          this.hasOnlyOneVariant ||
          (this.hasOnlyOneAvailableVariant && !this.variantsShowQatcOverlay) ||
          (!this.useProductState && !this.variantsShowQatcOverlay))
      ) {
        await this.handleAddToCart();
      } else {
        // Reusable Kameleoon trigger for Quick ATC targeting
        triggerKameleoonEvent('OpenQuickATC');

        // trigger extra event for Quick ATC on Designer Whey, Isoclear and
        // Flexpresso
        if (
          [9117171876155, 9117171515707, 9117171450171].includes(
            this.product.id,
          )
        ) {
          triggerKameleoonEvent('OpenQuickATCTop3Proteins');
        }

        this.openOverlay({
          callback: () => this.resetSelectedVariant(),
          ignoreDismissed: true,
          namespace: this.modalNamespace,
        });

        // Fire-and-forget: only relevant for products with 250+ variants,
        // the overlay is fully usable off the initial page while this
        // resolves.
        this.loadRemainingVariantsIfNeeded();
      }
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      let depositVariantId;
      if (!this.selectedVariant) {
        return;
      }

      /**
       * Notify me.
       * - Uses child component method.
       */
      if (!this.selectedVariant.available) {
        try {
          this.state = 'add_notify';
          await this.$refs.notifyMe.handleNotifyMe();
          this.state = 'ready';
        } catch (error) {
          this.state = 'error';
          cnvs.ReportError('Failed to submit notify me', error);
        }

        return;
      }

      /**
       * Local add.
       * - Skips the real cart entirely: the caller owns adding the resolved
       *   variant to its own local state (e.g. the mix & match box).
       * - Captures the variant before closing: closing the overlay runs its
       *   `callback` (`resetSelectedVariant`) synchronously, which would
       *   otherwise reset `this.selectedVariant` back to the default variant
       *   before the emit below reads it.
       */
      if (this.disableCartAdd) {
        if (this.selectedVariantLimitHint) {
          return;
        }

        const confirmedVariant = this.selectedVariant;
        this.closeOverlay(this.modalNamespace);
        this.$emit('confirm-local-add', confirmedVariant);
        this.state = 'ready';
        return;
      }

      const item = {
        id: this.selectedVariant.id,
        quantity: 1,
        hasLocalizedImage: Boolean(this.selectedVariant.localizedImage),
        productId: this.product.id,
        productMaxQuantity: this.product.maxQuantity,
        variantMaxQuantity: this.selectedVariant.maxQuantity,
      };

      if (this.freebieHandle) {
        item.properties = {
          [this.$variable('discount.freebies.property')]: this.freebieHandle,
        };
      }

      if (this.freebieGroupUid) {
        if (!item.properties) {
          item.properties = {};
        }
        item.properties[this.$variable('discount.freebies.propertyGroup')] =
          this.freebieGroupUid;
      }

      const items = [item];

      if (
        this.selectedVariant.canDeposit === 'true' &&
        this.$variable('store.country') === 'DE'
      ) {
        depositVariantId = this.selectedVariant.id;
        this.setDepositItemUpdate([
          ...this.depositItemUpdate,
          depositVariantId,
        ]);
        const depositProduct = {
          id: this.$variable('product.depositProductID'),
          quantity: this.selectedVariant.canDepositAmount,
        };
        items.push(depositProduct);
      }

      if (!this.product.hasOnlyDefaultVariant) {
        this.closeOverlay(this.modalNamespace);
      }

      /**
       * Add to cart.
       */
      try {
        this.state = 'adding';
        await this.addItem(items);
        this.state = 'added';
        if (this.showPageToast) {
          this.showPageToastSuccess();
        } else if (this.showCartToast) {
          this.showCartToastSuccess();
        }

        // Tracking event add qty to item in cart
        this.handleTrackingAddItem();

        // ESN-58: bubble event up to trigger correct goal
        this.$emit('added-to-cart', this.slideIndex);

        await this.resetState();
      } catch (error) {
        this.state = 'error';

        // Handle maxQuantity errors
        if (error.maxQuantityError) {
          const message = getMaxQuantityErrorMessage(
            error.maxQuantityType,
            error.maxQuantityValue,
          );

          this.addToast({
            title: message,
            id: 'max-quantity-limit-error',
            type: 'error',
          });
          this.state = 'ready';
          await this.resetState();
          return;
        }

        if (this.showPageToast) {
          this.showPageToastError(error.description);
        }

        await this.resetState();

        if (error.description) {
          return;
        }

        cnvs.ReportError('Failed to add product sample to cart', error);
      }

      this.setDepositItemUpdate(
        this.depositItemUpdate.filter((id) => id !== depositVariantId),
      );
    },

    /**
     * Handle update on the notify me component - if requests has success.
     * @param {Boolean} success - Wether the request was successful or not.
     */
    handleNotifyMeSuccess(success) {
      if (success) {
        this.showCta = false;
      } else {
        this.state = 'error';
      }
    },

    /**
     * If add was successful, show success toast.
     */
    showPageToastSuccess() {
      this.addToast({
        title: this.$string('toast.quick_add_success'),
        id: 'quick-add-success',
        ctaText: this.$string('toast.open_cart'),
        type: 'success',
        callback: () => {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          });
        },
      });
    },

    /**
     * if somethign went wrong, show error toast with response message.
     * @param {String} errorMessage - Error description
     */
    showPageToastError(errorMessage) {
      this.addToast({
        title: errorMessage,
        id: 'quick-add-error',
        ctaText: this.$string('toast.open_cart'),
        type: 'error',
        callback: () => {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          });
        },
      });
    },

    showCartToastSuccess() {
      this.addToast({
        id: 'freebie-manual-add-success',
        type: 'success',
        customClass: 'drawer',
        showCheckMark: false,
        title: this.$string('cart.freebies.added_message'),
      });
    },

    /**
     * Tracking event add qty to item in cart.
     */
    handleTrackingAddItem() {
      const campaignDiscount = this.getCampaignDiscount(
        this.getDiscountLogic(this.selectedVariant),
        'active',
      );
      const discountObject = {
        discountAmount: campaignDiscount?.amount,
        calculationType: campaignDiscount?.calculationType,
        discountCode: this.getCartAttributeCode(),
      };

      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');

      const item = formatTrackingItem({
        product: this.product,
        variant: this.variant,
        template: 'Quick Add To Cart',
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: this.quantity,
      });

      cnvs.EventBus.emit('dataLayer:add_to_cart', {
        event: 'add_to_cart',
        currency: cnvs.store.currency,
        location: this.location,
        items: [item],
      });
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'));
      });

      this.state = 'ready';
    },
  },
};
</script>

<style lang="scss">
@import './quick-add';
</style>
